import { useState, useEffect } from 'react';
import { Wrench, Eye, Crown, Megaphone, ArrowRight, Sparkles, Star, Users, ShoppingBag, Clock, Heart, StarOff, Terminal, Zap, Grid3X3, Globe2, Lock, BookOpen, Bot, Code, FileText, Palette } from 'lucide-react';
import GlowCard from '@/components/GlowCard';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getToolDisplayName, getToolPath, getToolEmoji } from '@/lib/tool-utils';


const TOTAL_TOOLS = 37; // Real count from sidebar categories

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'success' | 'warning' | 'danger';
  created_at: string;
}

const typeColors = {
  info: 'border-primary/50 bg-primary/5',
  success: 'border-success/50 bg-success/5',
  warning: 'border-warning/50 bg-warning/5',
  danger: 'border-destructive/50 bg-destructive/5',
};

const newTools = [
  { name: 'Code Studio', description: 'Build & deploy websites', path: '/dashboard/tools/code-studio', icon: Terminal, isNew: false, isImproved: true },
  { name: 'AI Humanizer', description: 'Humanize AI text', path: '/dashboard/tools/ai-humanizer', icon: Sparkles, isNew: true },
  { name: 'Prompt Generator', description: 'Create AI prompts', path: '/dashboard/tools/prompt-generator', icon: Sparkles, isNew: true },
  { name: 'Screenshot Beautifier', description: 'Beautify screenshots', path: '/dashboard/tools/screenshot-beautifier', icon: Eye, isNew: true },
  { name: 'QR Code Generator', description: 'Generate QR codes', path: '/dashboard/tools/qr-code', icon: Sparkles, isNew: true },
];

const Dashboard = () => {
  const { username, isPremium } = useProfile();
  const { user } = useAuth();
  const [latestAnnouncement, setLatestAnnouncement] = useState<Announcement | null>(null);
  const [recentTools, setRecentTools] = useState<{ tool_name: string; used_at: string }[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [realStats, setRealStats] = useState({ views: 0, users: 0, catalogue: 0, monthlyViews: 0 });
  const [animatedStats, setAnimatedStats] = useState({ tools: 0, visits: 0, users: 0, catalogue: 0 });

  useEffect(() => {
    fetchLatestAnnouncement();
    fetchRealStats();
    if (user) {
      fetchRecentTools();
      loadFavorites();
    }
  }, [user]);

  // Animate stats when realStats are loaded
  useEffect(() => {
    // PAGE VIEWS = real monthly views squared
    const monthlyViews = realStats.monthlyViews || 0;
    const targets = {
      tools: TOTAL_TOOLS,
      visits: monthlyViews * monthlyViews,
      users: realStats.users,
      catalogue: realStats.catalogue,
    };
    const duration = 1500;
    const steps = 60;
    const stepDuration = duration / steps;
    let step = 0;

    const animateInterval = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setAnimatedStats({
        tools: Math.floor(targets.tools * easeOut),
        visits: Math.floor(targets.visits * easeOut),
        users: Math.floor(targets.users * easeOut),
        catalogue: Math.floor(targets.catalogue * easeOut),
      });
      if (step >= steps) {
        clearInterval(animateInterval);
        setAnimatedStats(targets);
      }
    }, stepDuration);

    return () => clearInterval(animateInterval);
  }, [realStats]);

  const fetchRealStats = async () => {
    try {
      const { data, error } = await (supabase.rpc as any)('get_platform_stats') as { data: any; error: any };
      if (!error && data) {
        setRealStats({
          views: data.total_views || 0,
          users: data.total_users || 0,
          catalogue: data.total_products || 0,
          monthlyViews: data.monthly_views || 0,
        });
      }
    } catch (e) {
      console.error('Failed to fetch real stats:', e);
    }
  };

  const fetchLatestAnnouncement = async () => {
    const { data, error } = await supabase
      .from('announcements')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!error && data) {
      setLatestAnnouncement(data as Announcement);
    }
  };

  const fetchRecentTools = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('tool_usage')
      .select('tool_name, used_at')
      .eq('user_id', user.id)
      .order('used_at', { ascending: false })
      .limit(5);
    if (data) setRecentTools(data);
  };

  const loadFavorites = () => {
    const saved = localStorage.getItem('tam_favorites');
    if (saved) setFavorites(JSON.parse(saved));
  };

  const toggleFavorite = (toolName: string) => {
    setFavorites(prev => {
      const next = prev.includes(toolName)
        ? prev.filter(f => f !== toolName)
        : [...prev, toolName];
      localStorage.setItem('tam_favorites', JSON.stringify(next));
      toast.success(next.includes(toolName) ? `Added ${getToolDisplayName(toolName)} to favorites` : `Removed from favorites`);
      return next;
    });
  };

  const toSafeDate = (value?: string | null) => {
    if (!value || typeof value !== 'string') return null;

    const normalized = value.trim();
    if (!normalized) return null;

    const candidates = [
      normalized,
      normalized.replace(' ', 'T'),
      normalized.endsWith('Z') ? normalized : `${normalized}Z`,
      normalized.replace(' ', 'T').replace(/\+00$/, 'Z'),
      normalized.replace(' ', 'T').replace(/\+00:00$/, 'Z'),
    ];

    for (const candidate of candidates) {
      const parsed = parseISO(candidate);
      if (isValid(parsed)) return parsed;

      const fallback = new Date(candidate);
      if (!Number.isNaN(fallback.getTime())) return fallback;
    }

    return null;
  };

  const getRelativeUsedAt = (usedAt?: string | null) => {
    const parsedDate = toSafeDate(usedAt);
    return parsedDate ? formatDistanceToNow(parsedDate, { addSuffix: true }) : 'recently';
  };

  const stats = [
    { label: 'TOOLS AVAILABLE', value: `${animatedStats.tools}`, icon: Wrench, color: 'text-primary' },
    { label: 'PAGE VIEWS', value: animatedStats.visits.toLocaleString(), icon: Eye, color: 'text-emerald-400' },
    { label: 'REGISTERED USERS', value: animatedStats.users.toLocaleString(), icon: Users, color: 'text-blue-400' },
    { label: 'CATALOGUE ITEMS', value: animatedStats.catalogue.toString(), icon: ShoppingBag, color: 'text-warning' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Announcement Banner */}
      {latestAnnouncement && (
        <div className={cn("p-4 rounded-xl border-2 flex items-center gap-4 animate-slide-up", typeColors[latestAnnouncement.type])}>
          <div className="p-2 rounded-lg bg-background/50">
            <Megaphone className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold">{latestAnnouncement.title}</h4>
            <p className="text-sm text-muted-foreground line-clamp-1">{latestAnnouncement.content}</p>
          </div>
            <span className="text-xs text-muted-foreground hidden sm:block">
              {getRelativeUsedAt(latestAnnouncement.created_at)}
            </span>
        </div>
      )}

      {/* Hero Welcome Section */}
      <div className="relative overflow-hidden rounded-2xl border border-border/40 bg-gradient-to-br from-primary/10 via-accent/5 to-transparent p-6 sm:p-8">
        {/* Shimmer overlay */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/20 via-transparent to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-to-tl from-accent/15 via-transparent to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          {/* Glitter particles */}
          <div className="absolute top-[10%] left-[20%] w-1 h-1 bg-primary/60 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
          <div className="absolute top-[30%] right-[15%] w-1.5 h-1.5 bg-blue-400/50 rounded-full animate-ping" style={{ animationDuration: '3s', animationDelay: '0.5s' }} />
          <div className="absolute bottom-[20%] left-[40%] w-1 h-1 bg-violet-400/50 rounded-full animate-ping" style={{ animationDuration: '2.5s', animationDelay: '1s' }} />
          <div className="absolute top-[50%] left-[70%] w-0.5 h-0.5 bg-cyan-400/60 rounded-full animate-ping" style={{ animationDuration: '1.8s', animationDelay: '0.3s' }} />
          <div className="absolute top-[15%] right-[35%] w-1 h-1 bg-fuchsia-400/40 rounded-full animate-ping" style={{ animationDuration: '2.8s', animationDelay: '1.5s' }} />
          <div className="absolute bottom-[35%] left-[10%] w-0.5 h-0.5 bg-primary/50 rounded-full animate-ping" style={{ animationDuration: '3.5s', animationDelay: '0.8s' }} />
        </div>

        <div className="relative z-10">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">Welcome to</p>
          <h1 className="text-4xl sm:text-5xl font-black mb-1 bg-gradient-to-r from-foreground via-foreground to-primary bg-clip-text text-transparent">
            TAM Store
          </h1>
          <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-primary via-blue-400 to-violet-400 bg-clip-text text-transparent mb-3">
            Your Ultimate Toolkit
          </h2>
          <p className="text-muted-foreground text-sm max-w-lg">
            Your ultimate toolkit for digital operations. Access {TOTAL_TOOLS} premium tools at your fingertips.
          </p>
        </div>

        {/* Stats Row — inside hero */}
        <div className="relative z-10 grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border/30">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center group">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-background/50 backdrop-blur-sm border border-border/30 mb-2 group-hover:border-primary/40 group-hover:shadow-lg group-hover:shadow-primary/10 transition-all duration-300">
                <stat.icon className={cn("h-5 w-5", stat.color)} />
              </div>
              <p className="text-2xl sm:text-3xl font-bold tabular-nums">{stat.value}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Access — Mobile-friendly navigation tiles */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Grid3X3 className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Quick Access</h2>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <Link to="/dashboard/tools">
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border/50 bg-accent/20 hover:bg-accent/40 hover:border-primary/30 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md text-center group">
              <div className="p-2.5 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <Grid3X3 className="h-5 w-5 text-primary" />
              </div>
              <span className="text-xs font-medium">All Tools</span>
            </div>
          </Link>
          {isPremium && (
            <Link to="/dashboard/tools/live-sports" className="col-span-2 sm:col-span-1">
              <div className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-red-500/40 bg-gradient-to-br from-red-500/10 to-orange-500/5 hover:from-red-500/20 hover:to-orange-500/10 hover:border-red-500/60 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg hover:shadow-red-500/10 text-center group relative overflow-hidden">
                <div className="absolute top-1 right-1">
                  <span className="text-[9px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full animate-pulse">LIVE</span>
                </div>
                <div className="p-2.5 rounded-xl bg-red-500/15 group-hover:bg-red-500/25 transition-colors">
                  <Zap className="h-6 w-6 text-red-400" />
                </div>
                <span className="text-sm font-bold text-red-400">Live Sports</span>
                <span className="text-[10px] text-muted-foreground">Cricket & Football</span>
              </div>
            </Link>
          )}
          <Link to="/dashboard/pro-tools">
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border/50 bg-accent/20 hover:bg-accent/40 hover:border-primary/30 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md text-center group">
              <div className="p-2.5 rounded-xl bg-yellow-500/10 group-hover:bg-yellow-500/20 transition-colors">
                <Crown className="h-5 w-5 text-yellow-400" />
              </div>
              <span className="text-xs font-medium">Pro Tools</span>
            </div>
          </Link>
          <Link to="/dashboard/tools/code-studio">
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border/50 bg-accent/20 hover:bg-accent/40 hover:border-primary/30 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md text-center group">
              <div className="p-2.5 rounded-xl bg-violet-500/10 group-hover:bg-violet-500/20 transition-colors">
                <Code className="h-5 w-5 text-violet-400" />
              </div>
              <span className="text-xs font-medium">Code Studio</span>
            </div>
          </Link>
          <Link to="/dashboard/tools/ai-humanizer">
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border/50 bg-accent/20 hover:bg-accent/40 hover:border-primary/30 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md text-center group">
              <div className="p-2.5 rounded-xl bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                <Bot className="h-5 w-5 text-emerald-400" />
              </div>
              <span className="text-xs font-medium">AI Humanizer</span>
            </div>
          </Link>
          <Link to="/dashboard/my-sites">
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border/50 bg-accent/20 hover:bg-accent/40 hover:border-primary/30 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md text-center group">
              <div className="p-2.5 rounded-xl bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                <Globe2 className="h-5 w-5 text-blue-400" />
              </div>
              <span className="text-xs font-medium">My Sites</span>
            </div>
          </Link>
          <Link to="/dashboard/vault">
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border/50 bg-accent/20 hover:bg-accent/40 hover:border-primary/30 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md text-center group">
              <div className="p-2.5 rounded-xl bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                <Lock className="h-5 w-5 text-orange-400" />
              </div>
              <span className="text-xs font-medium">Vault</span>
            </div>
          </Link>
          <Link to="/dashboard/tools/pdf-analyzer">
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border/50 bg-accent/20 hover:bg-accent/40 hover:border-primary/30 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md text-center group">
              <div className="p-2.5 rounded-xl bg-cyan-500/10 group-hover:bg-cyan-500/20 transition-colors">
                <FileText className="h-5 w-5 text-cyan-400" />
              </div>
              <span className="text-xs font-medium">PDF Tools</span>
            </div>
          </Link>
          <Link to="/dashboard/guide">
            <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border/50 bg-accent/20 hover:bg-accent/40 hover:border-primary/30 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md text-center group">
              <div className="p-2.5 rounded-xl bg-pink-500/10 group-hover:bg-pink-500/20 transition-colors">
                <BookOpen className="h-5 w-5 text-pink-400" />
              </div>
              <span className="text-xs font-medium">Guide</span>
            </div>
          </Link>
        </div>
      </div>

      {/* New Tools Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">New Tools</h2>
          </div>
          <Link to="/dashboard/tools" className="text-sm text-primary flex items-center gap-1 hover:underline group">
            View All <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {newTools.map((tool) => (
            <Link
              key={tool.path}
              to={tool.path}
              {...(tool.name === 'Code Studio' ? { 'data-tour': 'code-studio' } : {})}
            >
              <div className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-accent/20 hover:bg-accent/40 hover:border-primary/30 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md cursor-pointer group">
                <div className="p-2 rounded-lg bg-primary/10">
                  <tool.icon className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold truncate">{tool.name}</p>
                    {tool.isNew && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[9px] px-1.5 py-0 h-4">NEW</Badge>
                    )}
                    {(tool as any).isImproved && (
                      <Badge className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[9px] px-1.5 py-0 h-4 border-0 whitespace-nowrap">✨ New & Improved</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{tool.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Favorites & Recently Used */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Favorites */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-5 w-5 text-warning" />
            <h2 className="text-lg font-semibold">Favorites</h2>
          </div>
          {favorites.length > 0 ? (
            <div className="space-y-2">
              {favorites.map((toolName) => (
                <Link key={toolName} to={getToolPath(toolName)}>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/20 hover:bg-accent/40 transition-all duration-200 group">
                    <span className="text-lg">{getToolEmoji(toolName)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{getToolDisplayName(toolName)}</p>
                        {(toolName === 'code-studio' || toolName === 'code_studio') && (
                          <Badge className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[9px] px-1.5 py-0 h-4 border-0 whitespace-nowrap">✨ New & Improved</Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-warning hover:text-destructive"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(toolName); }}
                    >
                      <StarOff className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-6 rounded-xl border border-border/50 bg-accent/10 text-center">
              <Heart className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Star tools from Recently Used to add favorites</p>
            </div>
          )}
        </div>

        {/* Recently Used */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Recently Used</h2>
          </div>
          {recentTools.length > 0 ? (
            <div className="space-y-2">
              {recentTools.map((tool, i) => (
                <Link key={i} to={getToolPath(tool.tool_name)}>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/20 hover:bg-accent/40 transition-all duration-200 group">
                    <span className="text-lg">{getToolEmoji(tool.tool_name)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{getToolDisplayName(tool.tool_name)}</p>
                        {(tool.tool_name === 'code-studio' || tool.tool_name === 'code_studio') && (
                          <Badge className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[9px] px-1.5 py-0 h-4 border-0 whitespace-nowrap">✨ New & Improved</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {getRelativeUsedAt(tool.used_at)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-7 w-7 shrink-0",
                        favorites.includes(tool.tool_name) ? "text-warning" : "text-muted-foreground opacity-0 group-hover:opacity-100"
                      )}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(tool.tool_name); }}
                    >
                      <Star className={cn("h-3.5 w-3.5", favorites.includes(tool.tool_name) && "fill-warning")} />
                    </Button>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-6 rounded-xl border border-border/50 bg-accent/10 text-center">
              <Clock className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Tools you use will appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/dashboard/tools">
          <GlowCard className="card-lift cursor-pointer h-full">
            <h3 className="font-semibold mb-2">🚀 Browse All {TOTAL_TOOLS} Tools</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Explore our complete toolkit and get started right away.
            </p>
            <span className="text-sm text-primary flex items-center gap-1">
              Browse Tools <ArrowRight className="h-4 w-4" />
            </span>
          </GlowCard>
        </Link>

        <Link to="/dashboard/catalogue">
          <GlowCard className="card-lift cursor-pointer h-full">
            <h3 className="font-semibold mb-2">🛒 Browse Catalogue</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Check out our digital products and premium offerings.
            </p>
            <span className="text-sm text-primary flex items-center gap-1">
              View Catalogue <ArrowRight className="h-4 w-4" />
            </span>
          </GlowCard>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
